import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle, Loader2 } from 'lucide-react';
import ProductPurchaseControl from './ProductPurchaseControl';
import { submissionKey, mapPurchaseHttpError } from '../../lib/purchaseMode';

/**
 * D18-D — tenant-safe Business credits & usage transparency. Read-only; every figure comes from the
 * server projection /billing/me/commercial-summary (ledger-derived, secret-free). No hardcoded prices/rates,
 * no raw IDs, no cross-tenant data. Two SEPARATE wallets are always shown (never one combined-only balance).
 */
const Wallet = ({ title, w }) => (
  <Card className="bg-white shadow-md">
    <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent>
      <dl className="grid grid-cols-2 gap-y-1 text-sm">
        <dt className="text-gray-500">Included this period</dt><dd className="text-right tabular-nums">{w.included_this_period}</dd>
        <dt className="text-gray-500">Used this period</dt><dd className="text-right tabular-nums">{w.used_this_period}</dd>
        <dt className="text-gray-500">Remaining</dt><dd className="text-right tabular-nums font-semibold">{w.remaining}</dd>
        <dt className="text-gray-500">Purchased remaining</dt><dd className="text-right tabular-nums">{w.purchased_remaining}</dd>
        <dt className="text-gray-500">Grace ({w.grace_cap} cap)</dt><dd className="text-right tabular-nums">{w.grace_used} used / {w.grace_available} left</dd>
        <dt className="text-gray-500">Next reset</dt><dd className="text-right">{String(w.next_reset || '').slice(0, 10)}</dd>
        <dt className="text-gray-500">Earliest pack expiry</dt><dd className="text-right">{w.earliest_purchased_expiry ? String(w.earliest_purchased_expiry).slice(0, 10) : '—'}</dd>
      </dl>
    </CardContent>
  </Card>
);

const BusinessCreditsUsage = ({ makeAuthenticatedRequest }) => {
  const [state, setState] = useState('loading'); // loading | ok | unavailable | error
  const [data, setData] = useState(null);
  const [purchasable, setPurchasable] = useState({ credit_packs: [], usage_packs: [], purchase_controls_enabled: false });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const loadPurchasable = async () => {
    try {
      const r = await makeAuthenticatedRequest('/api/v1/billing/me/commercial/purchasable');
      if (r && r.ok) setPurchasable(await r.json());
    } catch (_) { /* projection is best-effort; wallets/rate-card still render */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await makeAuthenticatedRequest('/api/v1/billing/me/commercial-summary');
        if (!r || !r.ok) { setState('error'); return; }         // failed read is NOT shown as empty success
        const d = await r.json();
        setData(d);
        setState(d?.has_agreement ? 'ok' : 'unavailable');
        await loadPurchasable();
      } catch (_) { setState('error'); }
    })();
    /* eslint-disable-next-line */
  }, []);

  // DIRECT pack Checkout (credit or usage). The browser only navigates to the Stripe TEST URL — it NEVER grants
  // credits/capacity or creates a receipt locally; fulfilment is the signed webhook + a later refetch.
  const purchasePack = async (env) => {
    if (busyKey) return;                          // one in-flight submission (double-click guard)
    setBusyKey(submissionKey(env)); setToast(null);
    try {
      const res = await makeAuthenticatedRequest('/api/v1/billing/pack/checkout', {
        method: 'POST', body: JSON.stringify({ pack_id: env.product_key, origin_url: window.location.origin }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const m = mapPurchaseHttpError(res.status, d?.detail?.code, d?.detail?.reason_message || d?.detail?.message);
        setToast({ ok: false, msg: m.message }); return;
      }
      const d = await res.json();
      if (d.url) { window.location.assign(d.url); return; }
      setToast({ ok: true, msg: 'Checkout is being prepared…' });
    } catch (_) { setToast({ ok: false, msg: 'Network error. Please retry.' }); }
    finally { setBusyKey(null); }
  };

  if (state === 'loading') return <div className="flex items-center gap-2 text-gray-500 text-sm p-4"><Loader2 className="h-4 w-4 animate-spin" />Loading credits &amp; usage…</div>;
  if (state === 'error') return <div className="flex items-center gap-2 p-3 rounded border bg-red-50 border-red-200 text-red-700 text-sm"><AlertTriangle className="h-4 w-4" />Could not load your credits &amp; usage right now. Please retry.</div>;
  if (state === 'unavailable') return <div className="p-3 rounded border bg-amber-50 border-amber-200 text-amber-800 text-sm">No active commercial agreement is mapped to your account yet.</div>;

  const ai = data.wallets.ai_operations;
  const comm = data.wallets.communication;
  const rc = data.rate_card;

  return (
    <div className="space-y-4">
      {/* Two separate wallets + convenience combined total (only alongside both) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Wallet title="AI & Operations Credits" w={ai} />
        <Wallet title="Communication Credits" w={comm} />
      </div>
      <p className="text-xs text-gray-500">Combined remaining (for reference): {ai.remaining + comm.remaining} — {ai.remaining} AI/Operations + {comm.remaining} Communication.
        Plan credits reset monthly and do not roll over; purchased packs expire after {data.credit_policy.purchased_pack_expiry_months} months.
        A disclosed {data.credit_policy.grace_percent}% grace applies per wallet; core platform access is never suspended for credit exhaustion; there is no silent invoiced overage.</p>

      {/* Activity rate card */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-2"><CardTitle className="text-base">Activity Rate Card (v{rc.version})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">{rc.ai_voice_disclosure}</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500 border-b"><th className="py-1 pr-3">Activity</th><th className="pr-3">Unit</th><th className="pr-3">Wallet</th><th className="pr-3">Credits</th></tr></thead>
            <tbody>{rc.activities.map((a, i) => (
              <tr key={i} className="border-b last:border-0"><td className="py-1 pr-3">{a.display}</td><td className="pr-3 text-gray-600">{a.unit}</td>
                <td className="pr-3">{a.wallet}</td><td className="pr-3 tabular-nums">{a.credits}{a.advertised ? <span className="text-gray-400"> — {a.advertised}</span> : null}</td></tr>
            ))}</tbody>
          </table>
        </CardContent>
      </Card>

      {toast && <div className={`p-3 rounded-lg border text-sm ${toast.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`} role="status">{toast.msg}</div>}

      {/* Credit packs — purchase-mode driven (direct Purchase / Request review), server-authoritative */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-2"><CardTitle className="text-base">Credit Packs</CardTitle></CardHeader>
        <CardContent>
          {(purchasable.credit_packs || []).length === 0 ? <p className="text-xs text-gray-400">No credit packs are currently available.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {purchasable.credit_packs.map((env) => (
                <div key={env.product_key} className="border rounded p-3">
                  <p className="font-medium text-gray-900">{env.display_name || env.product_key}</p>
                  {env.fulfilment_effect && env.fulfilment_effect.credits != null &&
                    <p className="text-xs text-gray-500">{env.fulfilment_effect.credits} credits · {env.fulfilment_effect.wallet}{env.fulfilment_effect.expiry_months ? ` · expires ${env.fulfilment_effect.expiry_months} mo` : ''}</p>}
                  <ProductPurchaseControl env={env} canPurchase={purchasable.purchase_controls_enabled}
                    busyKey={busyKey} onPurchase={purchasePack} onRequestReview={purchasePack} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage packs — additional capacity to a named limit; NOT a plan replacement */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-2"><CardTitle className="text-base">Usage Capacity Packs</CardTitle></CardHeader>
        <CardContent>
          {(purchasable.usage_packs || []).length === 0 ? <p className="text-xs text-gray-400">No usage packs are currently available.</p> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {purchasable.usage_packs.map((env) => (
                <div key={env.product_key} className="border rounded p-3">
                  <p className="font-medium text-gray-900">{env.display_name || env.product_key}</p>
                  {env.fulfilment_effect && env.fulfilment_effect.granted_quantity != null &&
                    <p className="text-xs text-gray-500">+{env.fulfilment_effect.granted_quantity} {env.fulfilment_effect.unit} capacity (adds to your base allowance; does not replace your plan)</p>}
                  <ProductPurchaseControl env={env} canPurchase={purchasable.purchase_controls_enabled}
                    busyKey={busyKey} onPurchase={purchasePack} onRequestReview={purchasePack} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction fees + upcoming changes (honest empty) */}
      <Card className="bg-white shadow-md">
        <CardHeader className="pb-2"><CardTitle className="text-base">Marketplace &amp; Booking Fees</CardTitle></CardHeader>
        <CardContent>
          {data.transaction_fees.length === 0
            ? <p className="text-xs text-gray-400">No marketplace or booking fees currently apply to your account.</p>
            : <ul className="text-sm list-disc pl-5">{data.transaction_fees.map((f, i) => <li key={i}>{f.eligible_txn_type}: {f.fee_type}</li>)}</ul>}
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessCreditsUsage;
